import { useStore } from '@nanostores/react';
import { useState } from 'react';
import { chatId } from '~/lib/persistence/useChatHistory';
import { apiGetShop, type Shop } from '~/lib/api/shop';
import { LoadError, Pill, Section, Segmented, SkeletonCard } from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import { CollectionsView } from './CollectionsView';
import { InventoryView } from './InventoryView';
import { OrdersView } from './OrdersView';
import { ProductsView } from './ProductsView';
import { ShopSettingsView } from './ShopSettingsView';

type View = 'products' | 'inventory' | 'orders' | 'collections' | 'settings';

const VIEWS = [
  ['products', 'Products'],
  ['inventory', 'Inventory'],
  ['orders', 'Orders'],
  ['collections', 'Collections'],
  ['settings', 'Settings'],
] as const;

/**
 * Owner-facing Shop plugin panel. The app id IS the chat id; the shop is
 * created on first open (with the Payments currency), so it works before the
 * site is published. Each sub-view loads on its own.
 */
export function ShopPanel() {
  const appId = useStore(chatId);

  return (
    <div className="relative flex h-full flex-col">
      {appId ? (
        <ShopPanelBody key={appId} appId={appId} />
      ) : (
        <>
          <PanelHeader />
          <div className="flex flex-1 items-center justify-center px-6 text-center text-[12px] text-muted">
            Start building to set up your shop.
          </div>
        </>
      )}
    </div>
  );
}

function PanelHeader({ shop, children }: { shop?: Shop | null; children?: React.ReactNode }) {
  return (
    <div className="border-b border-hairline px-4 py-3.5">
      <div className="flex items-center justify-between gap-2.5">
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-accent/10 ring-1 ring-inset ring-accent/15">
            <div className="i-ph:storefront text-[13px] text-accent" />
          </div>
          <h3 className="truncate text-[13px] font-medium tracking-tight text-primary">{shop?.name || 'Shop'}</h3>
        </div>
        {shop && <Pill tone="muted" label={shop.currency} />}
      </div>
      {children}
    </div>
  );
}

function ShopPanelBody({ appId }: { appId: string }) {
  const shop = useResource(() => apiGetShop(appId), appId);
  const [view, setView] = useState<View>('products');

  return (
    <>
      <PanelHeader shop={shop.data}>
        <div className="mt-3">
          <Segmented label="Shop section" value={view} options={VIEWS} onChange={setView} />
        </div>
      </PanelHeader>

      <div className="flex-1 space-y-6 overflow-y-auto p-4">
        {shop.data ? (
          <>
            {view === 'products' && <ProductsView appId={appId} shop={shop.data} />}
            {view === 'inventory' && <InventoryView appId={appId} shop={shop.data} />}
            {view === 'orders' && <OrdersView appId={appId} />}
            {view === 'collections' && <CollectionsView appId={appId} shop={shop.data} />}
            {view === 'settings' && <ShopSettingsView appId={appId} shop={shop.data} onSaved={shop.setData} />}
          </>
        ) : shop.error ? (
          <LoadError message="Could not load your shop." onRetry={() => void shop.reload()} />
        ) : (
          <Section title="Shop" icon="i-ph:storefront">
            <SkeletonCard lines={3} />
            <SkeletonCard />
          </Section>
        )}
      </div>
    </>
  );
}
